/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import {error} from '@ion/core/src/logger/logger';
import {
  createAlertActionName,
  getCrashBoxEmailTemplate,
  getDefaultTemplateName,
  isAlertLanguageEn,
  updateAlertActionEntity
} from '@ion/core/src/services/AlertUtils';
import {
  createAlertAction,
  createNotification,
  deleteAlertAction,
  deleteNotification as destroyNotification,
  deleteNotifications as destroyNotifications,
  getAlertAction,
  getAlertActionTemplates,
  getAlertTypeTriggers,
  getNotification,
  getSingleNotificationType,
  loadNotifications,
  updateAlertAction,
  updateNotification,
  updateNotifications
} from '@ion/core/src/services/Connect';
import {entityId, getExtendedAttributeValue} from '@ion/core/src/services/ConnectUtils';
import {
  EditorMode
} from '@ion/core/src/types/editors';
import {
  AdminNotificationsFilters,
  AlertAction,
  AlertActionTemplate,
  AlertTrigger,
  AuditChange,
  Entity,
  EntityStatus,
  Link,
  Notification,
  Notifications
} from '@ion/core/src/types/entities';
import {
  LoadNotificationsParams,
  ServiceError
} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {isCloneMode} from '@ion/dls/src/editors/helpers';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {getGlobalProp} from '@ion/dls/src/props/GlobalPropsConfig';
import {getResellerName, Reseller} from '@ion/dls/src/theme/resellers/Resellers';
import {createModularSearchFilters} from '@ion/features/src/search/utils';
import {retryUpdate} from '@ion/services/src/helpers/retry';
import {PageState} from '@/types/pages';
import {
  AdminAction,
  AsyncState,
  PageInitializeAction
} from '@/types/redux';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns,
  reInitializeState
} from '../../../helpers';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {gridDefaultPageSize} from '../../../utils/helpers';
import {
  applyExtendedAttributeOverride,
  synchronizeNotificationEntities
} from './helpers';

export const resellerTemplateContext = {
  [Reseller.RACConnect]    : 'RAC',
  [Reseller.BrunoSchillig] : 'BS',
  [Reseller.Ayvens]        : 'AV'
};

export interface EditAdminNotificationParams {
  notification: Notification;
  show?: boolean;
  isViewMode?: boolean;
  editorMode?: EditorMode
};

/**
 * ACTIONS
 */
// name of the reducer
export const NAME                                         = 'adminNotifications';

export const INITIALIZE_ADMIN_NOTIFICATIONS               = 'INITIALIZE_ADMIN_NOTIFICATIONS';
export const RE_INITIALIZE_ADMIN_NOTIFICATIONS            = 'RE_INITIALIZE_ADMIN_NOTIFICATIONS';

// grid column state
export const SET_GRID_COLUMNS                             = 'SET_ADMIN_NOTIFICATIONS_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                          = 'UPDATE_ADMIN_NOTIFICATIONS_GRID_COLUMNS';
export const SET_ADMIN_NOTIFICATION_GRID_FILTER_MODEL     = 'SET_ADMIN_NOTIFICATION_GRID_FILTER_MODEL';

export const ADMIN_NOTIFICATIONS_GRID_REFRESHED           = 'ADMIN_NOTIFICATIONS_GRID_REFRESHED';

export const SET_VIEW_SUB_GROUPS                          = 'SET_ADMIN_NOTIFICATIONS_VIEW_SUB_GROUPS';

export const SET_GRID_PAGE_SIZE                           = 'SET_ADMIN_NOTIFICATIONS_GRID_PAGE_SIZE';

// notification retrieval ...
export const GET_ADMIN_NOTIFICATIONS_REQUEST              = 'GET_ADMIN_NOTIFICATIONS_REQUEST';
export const GET_ADMIN_NOTIFICATIONS_RESPONSE             = 'GET_ADMIN_NOTIFICATIONS_RESPONSE';
export const GET_ADMIN_NOTIFICATIONS_ERROR                = 'GET_ADMIN_NOTIFICATIONS_ERROR';

// notification add ...
export const ADD_ADMIN_NOTIFICATION                       = 'ADD_ADMIN_NOTIFICATION';
export const ADD_ADMIN_NOTIFICATION_REQUEST               = 'ADD_ADMIN_NOTIFICATION_REQUEST';
export const ADD_ADMIN_NOTIFICATION_RESPONSE              = 'ADD_ADMIN_NOTIFICATION_RESPONSE';
export const ADD_ADMIN_NOTIFICATION_ERROR                 = 'ADD_ADMIN_NOTIFICATION_ERROR';

// notification edit ...
export const EDIT_ADMIN_NOTIFICATION                      = 'EDIT_ADMIN_NOTIFICATION';
export const EDIT_ADMIN_NOTIFICATION_LOADING              = 'EDIT_ADMIN_NOTIFICATION_LOADING';
export const EDIT_ADMIN_NOTIFICATION_REQUEST              = 'EDIT_ADMIN_NOTIFICATION_REQUEST';
export const EDIT_ADMIN_NOTIFICATION_RESPONSE             = 'EDIT_ADMIN_NOTIFICATION_RESPONSE';
export const EDIT_ADMIN_NOTIFICATION_ERROR                = 'EDIT_ADMIN_NOTIFICATION_ERROR';

export const TOGGLE_NOTIFICATION_EDITOR_VIEW_MODE         = 'TOGGLE_NOTIFICATION_EDITOR_VIEW_MODE';

// notification edit (load notification) ...
export const GET_ADMIN_NOTIFICATION_REQUEST               = 'GET_ADMIN_NOTIFICATION_REQUEST';
export const GET_ADMIN_NOTIFICATION_RESPONSE              = 'GET_ADMIN_NOTIFICATION_RESPONSE';
export const GET_ADMIN_NOTIFICATION_ERROR                 = 'GET_ADMIN_NOTIFICATION_ERROR';

// notification delete ...
export const DELETE_ADMIN_NOTIFICATION                    = 'DELETE_ADMIN_NOTIFICATION';
export const DELETE_ADMIN_NOTIFICATION_REQUEST            = 'DELETE_ADMIN_NOTIFICATION_REQUEST';
export const DELETE_ADMIN_NOTIFICATION_RESPONSE           = 'DELETE_ADMIN_NOTIFICATION_RESPONSE';
export const DELETE_ADMIN_NOTIFICATION_ERROR              = 'DELETE_ADMIN_NOTIFICATION_ERROR';

// manage asset associations ...
export const MANAGE_ADMIN_NOTIFICATION                    = 'MANAGE_ADMIN_NOTIFICATION';
export const MANAGE_ADMIN_NOTIFICATION_REQUEST            = 'MANAGE_ADMIN_NOTIFICATION_REQUEST';
export const MANAGE_ADMIN_NOTIFICATION_RESPONSE           = 'MANAGE_ADMIN_NOTIFICATION_RESPONS  E';
export const MANAGE_ADMIN_NOTIFICATION_ERROR              = 'MANAGE_ADMIN_NOTIFICATION_ERROR';

// view asset audit log ...
export const AUDIT_ADMIN_NOTIFICATION                     = 'AUDIT_ADMIN_NOTIFICATION';
export const AUDIT_ADMIN_NOTIFICATION_REQUEST             = 'AUDIT_ADMIN_NOTIFICATION_REQUEST';
export const AUDIT_ADMIN_NOTIFICATION_RESPONSE            = 'AUDIT_ADMIN_NOTIFICATION_RESPONSE';
export const AUDIT_ADMIN_NOTIFICATION_ERROR               = 'AUDIT_ADMIN_NOTIFICATION_ERROR';
export const AUDIT_ADMIN_NOTIFICATION_SHOW_CHANGES_DIALOG = 'AUDIT_ADMIN_NOTIFICATION_SHOW_CHANGES_DIALOG';

// bulk notification edits ...
export const EDIT_ADMIN_NOTIFICATIONS                     = 'EDIT_ADMIN_NOTIFICATIONS';
export const EDIT_ADMIN_NOTIFICATIONS_REQUEST             = 'EDIT_ADMIN_NOTIFICATIONS_REQUEST';
export const EDIT_ADMIN_NOTIFICATIONS_RESPONSE            = 'EDIT_ADMIN_NOTIFICATIONS_RESPONSE';
export const EDIT_ADMIN_NOTIFICATIONS_ERROR               = 'EDIT_ADMIN_NOTIFICATIONS_ERROR';

// bulk notification deletes ...
export const DELETE_ADMIN_NOTIFICATIONS                   = 'DELETE_ADMIN_NOTIFICATIONS';
export const DELETE_ADMIN_NOTIFICATIONS_REQUEST           = 'DELETE_ADMIN_NOTIFICATIONS_REQUEST';
export const DELETE_ADMIN_NOTIFICATIONS_RESPONSE          = 'DELETE_ADMIN_NOTIFICATIONS_RESPONSE';
export const DELETE_ADMIN_NOTIFICATIONS_ERROR             = 'DELETE_ADMIN_NOTIFICATIONS_ERROR';

// search queries
export const UPDATE_ADMIN_NOTIFICATIONS_SEARCH_QUERIES    = 'UPDATE_ADMIN_NOTIFICATIONS_SEARCH_QUERIES';
export const RESET_ADMIN_NOTIFICATIONS_SEARCH_QUERIES     = 'RESET_ADMIN_NOTIFICATIONS_SEARCH_QUERIES';

/**
 * Initialize Notifications
 */
export const initializeAdminNotifications = (): PageInitializeAction => ({
  type : INITIALIZE_ADMIN_NOTIFICATIONS
});

export const reInitializeAdminNotifications = (): PageInitializeAction => ({
  type : RE_INITIALIZE_ADMIN_NOTIFICATIONS
});

/**
 *  Invoked when grid Refreshed
 */
export const adminNotificationsGridRefreshed = (): PageInitializeAction => ({
  type : ADMIN_NOTIFICATIONS_GRID_REFRESHED
});

// Updates the modular search queries for Admin Notifications
export const updateAdminNotificationsSearchQueries = (searchParams: AdminNotificationsFilters) => ({
  type : UPDATE_ADMIN_NOTIFICATIONS_SEARCH_QUERIES, searchParams
});

// Resets the Peg Labels filters state
export const resetAdminNotificationsSearchQueries = () => ({
  type : RESET_ADMIN_NOTIFICATIONS_SEARCH_QUERIES
});

// invokes when the fiter state changed in grid
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_NOTIFICATION_GRID_FILTER_MODEL, model
});

/**
 * getAdminNotifications return all notifications
 */
export const getAdminNotifications = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter = {},
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_NOTIFICATIONS_REQUEST});
    const {
      adminNotifications: {
        search
      }
    } = getState();
    const data: LoadNotificationsParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    data.filterOn = {
      ...data.filterOn,
      ...createModularSearchFilters(search)
    };
    try {
      const {notifications: items, totalCount} = await loadNotifications(data);
      dispatch({type: GET_ADMIN_NOTIFICATIONS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_NOTIFICATIONS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * Invoked when the Add Admin Notification editor is to be displayed or removed.
 */
export const addAdminNotification = (show = true) => ({
  type : ADD_ADMIN_NOTIFICATION, show
});

/**
 * Invoked when the the Notification auditor is to be displayed or removed.
 */
export const auditAdminNotification = (notification: Notification, show = true) => ({
  type : AUDIT_ADMIN_NOTIFICATION, notification, show
});

export const showAuditChangesDialog = (auditRecord: AuditChange, show = true) => ({
  type : AUDIT_ADMIN_NOTIFICATION_SHOW_CHANGES_DIALOG, auditRecord, show
});

/**
 * Invoked when a new notification is to be created; consolidating the process of Notification
 * creation into a single transaction. Notification creation is proxied through the
 * MicroServices which manage sending out the new notification confirmation emails,
 * etc.
 */
export const addNotification = (notification: Notification, actions?: AlertAction[]) => {
  return async(dispatch: Function, getState: Function) => {
    const {editorMode} = getState().adminNotifications;
    const isClone      = isCloneMode(editorMode);
    isClone && dispatch({type: EDIT_ADMIN_NOTIFICATION_LOADING, status: true});
    dispatch({type: ADD_ADMIN_NOTIFICATION_REQUEST, notification});
    try {
      const created = await createNotification(notification);
      let error;
      try {
        actions && (await createAlertActions(created, actions));
      } catch (err) {
        error = err;
      }
      dispatch({type: ADD_ADMIN_NOTIFICATION_RESPONSE, notification: created, editorMode});
      dispatch(toggleUserPrompt(
        error ? errorAddOptions(Entity.notification, error, true) : successAddOptions(Entity.notification, isClone)
      ));
    } catch (err) {
      dispatch({type: ADD_ADMIN_NOTIFICATION_ERROR, error: err, editorMode});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.notification, err, true)));
      error(err);
    }
    isClone && dispatch({type: EDIT_ADMIN_NOTIFICATION_LOADING, status: false});
  };
};

// Action creator for changing the view mode of the notification editor.
export const setNotificationEditorViewMode = (isViewMode: boolean = false, editorMode = EditorMode.View) => ({
  type : TOGGLE_NOTIFICATION_EDITOR_VIEW_MODE, isViewMode, editorMode
});

// Action creator for editing a notification.
export const editAdminNotificationAction = ({
  notification,
  show = true,
  isViewMode = false,
  editorMode = EditorMode.View
}: EditAdminNotificationParams) => ({
  type : EDIT_ADMIN_NOTIFICATION,
  editorMode,
  notification,
  isViewMode,
  show
});

/**
 * Invoked when the Edit Admin Notification editor is to be displayed or removed. When the
 * Editor is to be displayed, all auxilary data associated with the notification is retrieved.
 * This includes the current alert configuration (i.e. the notification), the Alert Actions,
 * and the Alert Type Triggers. The auxilary data is then persisted to the reducer and can
 * then be referenced by the editor for display.
 */
export const editAdminNotification = ({
  notification,
  show = true,
  isViewMode = false,
  editorMode = EditorMode.View
}: EditAdminNotificationParams): Function => {
  return async(dispatch: Function): Promise<any> => {
    if (notification?.alertConfigurationId) {
      notification = await getNotification(notification?.alertConfigurationId);
    }
    dispatch(editAdminNotificationAction({notification, show, isViewMode, editorMode}));
    if (show) {
      try {
        dispatch({type: GET_ADMIN_NOTIFICATION_REQUEST, notification});
        const configuration = await getNotification(notification?.id);
        const actions = await loadAlertActions(configuration?.alertActionLinks);
        dispatch({type: GET_ADMIN_NOTIFICATION_RESPONSE, configuration, actions});
      } catch (err) {
        dispatch({type: GET_ADMIN_NOTIFICATION_ERROR, error: err});
        dispatch(toggleUserPrompt(errorEditOptions(Entity.notification)));
        error(err);
      }
    }
  };
};

/**
 * Invoked when an existing notification is to be updated; consolidating the process of
 * Notification updates into a single transaction. The process for editing a Notification is
 * outlined below.
 *
 * 1.) Update the notification via CTC.
 * 2.) Update the provided Alert Actions via CTC. See updateAlertActions
 *
 */
export const editNotification = (
  notification: Notification,
  actions?: AlertAction[],
  isCtcAdmin?: boolean
): Function => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_NOTIFICATION_REQUEST});
    try {
      const updated = await retryUpdate({
        newEntity          : notification,
        updateEntity       : updateNotification,
        getEntity          : getSingleNotification,
        onError            : notificationUpdateError,
        useEntityFormatter : true,
        dispatch
      });
      if (updated) {
        const updatedAlertAction = await updateAlertActionWithRetry(
          applyExtendedAttributeOverride(notification, updated), dispatch, actions
        );
        if (!isUndefined(updatedAlertAction)) {
          dispatch(toggleUserPrompt(successEditOptions(Entity.notification)));
        }
        const updatedAlertConfig = await getNotification(updated.id);
        dispatch({type: EDIT_ADMIN_NOTIFICATION_RESPONSE, notification: updatedAlertConfig});
      }
    } catch (err) {
      notificationUpdateError(err, dispatch, true);
    }
  };
};

/**
 * Invoked when an existing notification is to be updated; consolidating the process of
 * Notification updates into a single transaction. The process for editing a Notification is
 * outlined below.
 *
 * 1.) Update the notification via CTC.
 * 2.) When notification receives Resource Out of State error GET latest notification via CTC.
 * 3.) Update the notification by passing latest notification details via CTC.
 * 4.) Update the provided Alert Actions via CTC. See updateAlertActions.
 *
 */
const updateAlertActionWithRetry = async(
  updatedNotification: Notification,
  dispatch: Function,
  actions?: AlertAction[]
) => {
  try {
    return await updateAlertActions(updatedNotification, actions);
  } catch (err) {
    const isConflict = isEqual(err?.errorCode, 409);
    if (isConflict) {
      for (const index in actions) {
        const {updated, error} = (await getSingleAlertAction(actions?.[index]?.id)) || {};
        if (updated) {
          actions[index].version = updated?.version;
        } else {
          notificationUpdateError(error, dispatch, true);
        }
      }
      try {
        return await updateAlertActions(updatedNotification, actions);
      } catch (error) {
        notificationUpdateError(error, dispatch, true);
      }
    } else {
      notificationUpdateError(err, dispatch, true);
    }
  }
};

/**
 * Invoked when an existing Alert Action gets resource out of state, delegating
 * to the fetch latest alert action details successful response or error action accordingly.
 */
const getSingleAlertAction = async(alertActionID: number) => {
  try {
    const alertAction = await getAlertAction(alertActionID);
    return {updated: alertAction, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Alert Notification gets resource out of state, delegating
 * to the fetch latest alert notification details successful response or error action accordingly.
 */
const getSingleNotification = async(notificationID: number) => {
  try {
    const notification = await getNotification(notificationID);
    return {updated: notification, error: ''};
  } catch (err) {
    error(err);
    return {updated: null, error: err};
  }
};

/**
 * Invoked when an existing Alert or Action Notification gets updated or fetched,
 * CTC API and dispatching a successful or error action accordingly.
 */
export const notificationUpdateError = (
  err,
  dispatch: Function,
  useEntityFormatter: boolean = false
) => {
  dispatch({type: EDIT_ADMIN_NOTIFICATION_ERROR, error: err});
  dispatch(toggleUserPrompt(errorEditOptions(Entity.notification, err, useEntityFormatter)));
  error(err);
};

/**
 * Invoked when the Bulk Edit Admin Notifications Dialog is to be displayed or removed.
 */
export const editAdminNotifications = (notifications: Notifications, show = true) => ({
  type : EDIT_ADMIN_NOTIFICATIONS, notifications, show
});

/**
 * Invoked when a collection of existing notifications are to be updated, delegating to
 * the updateNotifications CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const editNotifications = (notifications: Notifications, actions) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_NOTIFICATIONS_REQUEST});
    try {
      const {results}    = await updateNotifications(notifications);
      const postFetchIds = await bulkUpdateAlertActions(notifications, results, actions);
      const items = await Promise.allSettled(results.map(async({id}) => {
        if (postFetchIds[id]) {
          return await getNotification(id);
        }
      }));
      const updated = synchronizeNotificationEntities(items, results);
      dispatch(toggleUserPrompt(successEditOptions(Entity.notifications,
        {results, items: notifications})));
      dispatch({type: EDIT_ADMIN_NOTIFICATIONS_RESPONSE, notifications: updated});
    } catch (err) {
      dispatch({type: EDIT_ADMIN_NOTIFICATIONS_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.notifications)));
      error(err);
    }
  };
};

/**
 * Invoked when a collection of existing notification's action are
 * to be updated in bulk.
 */
export const bulkUpdateAlertActions = async(
  notifications: Notifications,
  results: Notifications,
  actions) => {
  const postFetchIds = {};
  // this will update the notification actions in bulk
  await Promise.all(results.map(async(result) => {
    const id   = result.id;
    const item = actions[id];
    if (item) {
      postFetchIds[id] = item.some(({status}) => status === EntityStatus.Enabled);
      const state = notifications.find(({id: notificationId}) => id === notificationId);
      return await updateAlertActions(
        applyExtendedAttributeOverride(state, result), item, false
      );
    }
  }));
  return postFetchIds;
};

/**
 * Invoked when the Delete Admin Notification Dialog is to be displayed or removed.
 */
export const deleteAdminNotification = (notification: Notification, show = true) => ({
  type : DELETE_ADMIN_NOTIFICATION, notification, show
});

/**
 * Invoked when an existing notification is to be deleted, delegating to the deleteNotification
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteNotification = (notification: Notification) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_NOTIFICATION_REQUEST});
    try {
      const deleted = await destroyNotification(notification);
      dispatch({type: DELETE_ADMIN_NOTIFICATION_RESPONSE, notification: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_NOTIFICATION_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Invoked when the Bulk Delete Admin Notifications Dialog is to be displayed or removed.
 */
export const deleteAdminNotifications = (notifications: Notifications, show = true) => ({
  type : DELETE_ADMIN_NOTIFICATIONS, notifications, show
});

/**
 * Invoked when a collection of existing notifications are to be deleted, delegating to
 * the destroyNotifications CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteNotifications = (notifications: Notifications) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_NOTIFICATIONS_REQUEST});
    try {
      const {results, errors} = await destroyNotifications(notifications);
      dispatch({type: DELETE_ADMIN_NOTIFICATIONS_RESPONSE, notifications: results});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_NOTIFICATIONS_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * Retrieves the collection of AlertTrigger associated with the given Alert Type.
 */
export const loadAlertTypeTriggers = async(alertTypeId: number): Promise<AlertTrigger[]> => {
  const triggers: AlertTrigger[] = await getAlertTypeTriggers(alertTypeId);
  return triggers;
};

/**
 * Retrieves the full entity representation for the given Alert Action Links.
 */
const loadAlertActions = async(actions: Link[], excludeDeleted = true): Promise<AlertAction[]> => {
  const items = excludeDeleted ? actions.filter(
    ({status}: Link) => status !== EntityStatus.Deleted
  ) : actions;
  return Promise.all(items.map(async(action: Link) => {
    return await getAlertAction(entityId(action));
  })) as Promise<AlertAction[]>;
};

/**
 * Manages the normalization of Alert Configuration Actions based on the associated
 * Alert Configuration, and creates them within the CTC Platform via createAlertAction.
 */
const createAlertActions = async(
  notification: Notification, actions: AlertAction[]
): Promise<AlertAction[]> => {
  const {account, _self} = notification;
  delete account.status;
  delete _self.status;
  _self.rel = 'alertConfiguration';
  actions = actions
    .filter(({status}: AlertAction) => {
      return status === EntityStatus.Enabled;
    });
  for (const index in actions) {
    actions[index] = await configureAlertAction(notification, actions[index]);
  }
  return Promise.all(actions.map(
    async(action) => await createAlertAction(action)
  ));
};

/**
 * Manages updating, deleting, and or creating of Alert Actions for a given Alert Configuration
 * (Notification)
 */
const updateAlertActions = async(
  notification: Notification, actions: AlertAction[], allowedToDelete = true
): Promise<AlertAction[]> => {
  return Promise.all(actions.map(async(action: AlertAction) => {
    if (action.id) {
      if (action.status === EntityStatus.Enabled) {
        return await updateAlertAction(await configureAlertAction(notification, action, true));
      } else if (allowedToDelete) {
        return await deleteAlertAction(action);
      }
      return null;
    }
    return await createAlertAction(await configureAlertAction(notification, action));
  }));
};

/**
 * Manages the normalization of an Alert Configuration (Notification) and it's associated
 * Actions prior to the creation of the Alert Actions.
 */
const configureAlertAction = async(
  alert: Notification,
  action: AlertAction,
  edit: boolean = false
): Promise<AlertAction> => {
  const reseller      = getResellerName();
  const {alertType}   = alert;
  const templateTitle = getCrashBoxEmailTemplate(alertType, action) ||
      ((!isAlertLanguageEn(alert) || reseller) ? await getDefaultTemplateName(alertType, action) : null);

  const useLegacyTemplateUpdate = getGlobalProp('NotificationEditor.useLegacyTemplateUpdate', false) as boolean;

  if (templateTitle === null && !useLegacyTemplateUpdate) {
    if (alertType?.title === 'FNL' && action?.actionType === 'EMAIL') {
      const template = await getAlertActionTemplates('ION-CRASHBOXX-EMAIL-TEMPLATE-EN');
      action.alertActionTemplate = {
        href : template?.[0]._self?.href,
        rel  : 'actionTemplate'
      };
    }
    action.name = createAlertActionName(alert, action);
    action.alertConfiguration = alert?._self;
    action.account = alert?.account;
    return action;
  } else {
    const resellerPrefix = reseller ? resellerTemplateContext[reseller] : null;
    const templateName   = resellerPrefix ? templateTitle?.replace(/^(AO-|ION-)/i, `${resellerPrefix}-`) : templateTitle;
    let templates: AlertActionTemplate[] | null = null;
    if (templateName) {
      try {
        templates = await getAlertActionTemplates(templateName);
      } catch (e) {
        error(e);
      }
      if (!templates?.length && resellerPrefix) {
        templates = await getAlertActionTemplates(templateTitle as any);
      }
    }
    return updateAlertActionEntity({
      alert,
      action,
      templates
    }, edit);
  }
};

/**
 * Invoked whenever user click on View Sub Accounts toggle button
 */
export const setAdminNotificationsViewSubGroups = (viewSubGroups: boolean = false) => ({
  type : SET_VIEW_SUB_GROUPS, viewSubGroups
});

/**
 * Invoked whenever user change the rows per page dropdown
 */
export const setAdminNotificationsGridPageSize = (pageSize: number) => ({
  type : SET_GRID_PAGE_SIZE, pageSize
});

/**
 * REDUCERS
 */
export interface AdminNotificationsAction extends AdminAction {
  notification?: Notification;
  notifications?: Notification[];
  searchParams?: AdminNotificationsFilters;
  editorMode?: EditorMode;
};

export interface AdminNotificationsEditAction extends AdminAction {
  notification?: Notification;
  notifications?: Notification[];
  isViewMode: boolean;
  editorMode?: EditorMode;
};

export interface AdminNotificationsActionState extends AsyncState {
  selected: Notification | Notification[] | null;
  result: Notification | Notification[] | null;
  record?: Notification | Notification[] | null;
  isEditing: boolean;
  isViewMode: boolean;
};

export interface AdminNotificationsAuditState extends AdminNotificationsActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
}

export interface AdminNotificationEditActionState extends AdminNotificationsActionState {
  actions: Notification | Notification[] | null;
  configuration: Notification;
};

export interface AdminNotificationsState extends PageState {
  notifications: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    includeSubGroups: boolean,
    filterModel: FilterModel | {},
    pageSize: number
  },
  add: AdminNotificationsActionState,
  edit: AdminNotificationEditActionState,
  audit: AdminNotificationsAuditState,
  destroy: AdminNotificationsActionState,
  bulkEdit: AdminNotificationsActionState,
  bulkDelete: AdminNotificationsActionState,
  search: AdminNotificationsFilters,
  editorMode: EditorMode
};

// defines the user preference key for the admin notifications page ...
export const PREFERENCE = 'admin-notifications';

// defines the initial state for the reducer ...
export const initialState: AdminNotificationsState = {
  isInitialized : false,
  notifications : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    includeSubGroups    : false,
    filterModel         : {},
    pageSize            : gridDefaultPageSize
  },
  add : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  edit : {
    isEditing     : false,
    selected      : null,
    loading       : false,
    error         : null,
    result        : null,
    actions       : null,
    configuration : null,
    isViewMode    : false
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    isViewMode        : false,
    showChangesDialog : false,
    auditRecord       : null
  },
  destroy : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  bulkEdit : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    isViewMode : false
  },
  bulkDelete : {
    isEditing  : false,
    selected   : null,
    loading    : false,
    error      : null,
    result     : null,
    record     : null,
    isViewMode : false
  },
  search : {
    status   : null,
    severity : null
  },
  editorMode : EditorMode.View
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_NOTIFICATIONS] : draft => {
    draft.isInitialized = true;
    const preference = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_NOTIFICATIONS] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_ADMIN_NOTIFICATION_GRID_FILTER_MODEL] : ({grid}: AdminNotificationsState, {model}) => {
    grid.filterModel = model;
  },
  [SET_GRID_COLUMNS] : ({grid, notifications}: AdminNotificationsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : notifications.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminNotificationsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_NOTIFICATIONS_GRID_REFRESHED] : (
    {add, edit, destroy, bulkDelete, bulkEdit}: AdminNotificationsState
  ) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
    bulkDelete.result = null;
    bulkEdit.result = null;
  },
  [GET_ADMIN_NOTIFICATIONS_REQUEST] : ({notifications}: AdminNotificationsState) => {
    notifications.loading = true;
  },
  [GET_ADMIN_NOTIFICATIONS_RESPONSE] : ({notifications}: AdminNotificationsState) => {
    notifications.loading = false;
  },
  [GET_ADMIN_NOTIFICATIONS_ERROR] : (
    {notifications}: AdminNotificationsState, {error}: AdminNotificationsAction
  ) => {
    notifications.loading = false;
    notifications.error = error;
  },

  [ADD_ADMIN_NOTIFICATION] : (draft: AdminNotificationsState, {show}: AdminNotificationsAction) => {
    draft.add.isEditing = show;
    draft.add.result    = null;
    draft.editorMode    = EditorMode.Add;
  },
  [ADD_ADMIN_NOTIFICATION_REQUEST] : ({add}: AdminNotificationsState) => {
    add.loading = true;
    add.error = add.result = null;
  },
  [ADD_ADMIN_NOTIFICATION_RESPONSE] : (
    {add, edit}: AdminNotificationsState, {notification, editorMode}: AdminNotificationsEditAction
  ) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = notification;
    if (isCloneMode(editorMode)) {
      edit.selected  = null;
      edit.isEditing = false;
    }
  },
  [ADD_ADMIN_NOTIFICATION_ERROR] : (
    {add, edit}: AdminNotificationsState,
    {error, editorMode}: AdminNotificationsAction
  ) => {
    add.loading = false;
    add.error   = error;
    if (isCloneMode(editorMode)) {
      edit.selected  = null;
      edit.isEditing = false;
    }
  },

  [EDIT_ADMIN_NOTIFICATION] : (
    draft: AdminNotificationsState, {notification, show, isViewMode, editorMode}: AdminNotificationsEditAction
  ) => {
    draft.edit.selected   = show ? notification : null;
    draft.edit.isEditing  = show;
    draft.edit.isViewMode = isViewMode;
    draft.editorMode      = editorMode;
    draft.edit.result     = null;
    if (!show) {
      draft.edit.configuration = draft.edit.actions = null;
    }
  },
  [EDIT_ADMIN_NOTIFICATION_LOADING] : ({edit}: AdminNotificationsState, {status}) => {
    edit.loading = status;
  },
  [TOGGLE_NOTIFICATION_EDITOR_VIEW_MODE] : (
    draft: AdminNotificationsState, {isViewMode, editorMode}: AdminNotificationsEditAction
  ) => {
    draft.edit.isViewMode = isViewMode;
    draft.editorMode      = editorMode;
  },
  [EDIT_ADMIN_NOTIFICATION_REQUEST] : ({edit}: AdminNotificationsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [EDIT_ADMIN_NOTIFICATION_RESPONSE] : (
    {edit}: AdminNotificationsState, {notification}: AdminNotificationsAction
  ) => {
    edit.loading = false;
    edit.isEditing = false;
    edit.result = notification;
  },
  [EDIT_ADMIN_NOTIFICATION_ERROR] : ({edit}: AdminNotificationsState, {error}: AdminNotificationsAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [GET_ADMIN_NOTIFICATION_REQUEST] : ({edit}: AdminNotificationsState) => {
    edit.loading = true;
    edit.error = edit.result = null;
  },
  [GET_ADMIN_NOTIFICATION_RESPONSE] : (
    {edit}: AdminNotificationsState, {configuration, actions}: AdminNotificationEditActionState
  ) => {
    edit.loading = false;
    edit.configuration = configuration;
    edit.actions = actions;
  },
  [GET_ADMIN_NOTIFICATION_ERROR] : ({edit}: AdminNotificationsState, {error}: AdminNotificationsAction) => {
    edit.loading = false;
    edit.error = error;
  },

  [AUDIT_ADMIN_NOTIFICATION] : ({audit}: AdminNotificationsState,
    {notification, show}: AdminNotificationsAction) => {
    audit.selected = show ? notification : null;
    audit.isEditing = show;
  },
  [AUDIT_ADMIN_NOTIFICATION_SHOW_CHANGES_DIALOG] : ({audit}: AdminNotificationsState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord = auditRecord;
  },
  [DELETE_ADMIN_NOTIFICATION] : (
    {destroy}: AdminNotificationsState, {notification, show}: AdminNotificationsAction
  ) => {
    destroy.selected = show ? notification : null;
    destroy.isEditing = show;
    destroy.result = null;
  },
  [DELETE_ADMIN_NOTIFICATION_REQUEST] : ({destroy}: AdminNotificationsState) => {
    destroy.loading = true;
    destroy.error = destroy.result = null;
  },
  [DELETE_ADMIN_NOTIFICATION_RESPONSE] : (
    {destroy}: AdminNotificationsState, {notification}: AdminNotificationsAction
  ) => {
    destroy.loading = false;
    destroy.isEditing = false;
    destroy.result = notification;
  },
  [DELETE_ADMIN_NOTIFICATION_ERROR] : (
    {destroy}: AdminNotificationsState, {error}: AdminNotificationsAction
  ) => {
    destroy.loading = false;
    destroy.error = error;
    destroy.result = null;
  },

  [EDIT_ADMIN_NOTIFICATIONS] : (
    {bulkEdit}: AdminNotificationsState, {notifications, show}: AdminNotificationsAction
  ) => {
    bulkEdit.selected = show ? notifications : null;
    bulkEdit.isEditing = show;
    bulkEdit.result = null;
  },
  [EDIT_ADMIN_NOTIFICATIONS_REQUEST] : ({bulkEdit}: AdminNotificationsState) => {
    bulkEdit.loading = true;
    bulkEdit.error = bulkEdit.result = null;
  },
  [EDIT_ADMIN_NOTIFICATIONS_RESPONSE] : (
    {bulkEdit}: AdminNotificationsState, {notifications}: AdminNotificationsAction
  ) => {
    bulkEdit.loading = false;
    bulkEdit.isEditing = false;
    bulkEdit.result = notifications;
  },
  [EDIT_ADMIN_NOTIFICATIONS_ERROR] : (
    {bulkEdit}: AdminNotificationsState, {error}: AdminNotificationsAction
  ) => {
    bulkEdit.loading = false;
    bulkEdit.error = error;
  },

  [DELETE_ADMIN_NOTIFICATIONS] : (
    {bulkDelete}: AdminNotificationsState, {notifications, show}: AdminNotificationsAction
  ) => {
    bulkDelete.selected = show ? notifications : null;
    bulkDelete.isEditing = show;
    bulkDelete.result = null;
    bulkDelete.record = null;
  },
  [DELETE_ADMIN_NOTIFICATIONS_REQUEST] : ({bulkDelete}: AdminNotificationsState) => {
    bulkDelete.loading = true;
    bulkDelete.error = bulkDelete.result = bulkDelete.record = null;
  },
  [DELETE_ADMIN_NOTIFICATIONS_RESPONSE] : (
    {bulkDelete}: AdminNotificationsState, {notifications}: AdminNotificationsAction
  ) => {
    bulkDelete.loading = false;
    bulkDelete.isEditing = false;
    bulkDelete.result = notifications;
    bulkDelete.record = notifications;
  },
  [DELETE_ADMIN_NOTIFICATIONS_ERROR] : (
    {bulkDelete}: AdminNotificationsState, {error}: AdminNotificationsAction
  ) => {
    bulkDelete.loading = false;
    bulkDelete.error = error;
  },
  [UPDATE_ADMIN_NOTIFICATIONS_SEARCH_QUERIES] : ({search}: AdminNotificationsState, {searchParams}) => {
    search.name     = searchParams.name || null;
    search.status   = searchParams.status || null;
    search.severity = searchParams.severity || null;
  },
  [RESET_ADMIN_NOTIFICATIONS_SEARCH_QUERIES] : ({search}: AdminNotificationsState) => {
    search.name     = null;
    search.status   = null;
    search.severity = null;
  },
  [SET_VIEW_SUB_GROUPS] : ({grid}: AdminNotificationsState, {viewSubGroups}) => {
    grid.includeSubGroups = viewSubGroups;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminNotificationsState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminNotificationsState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
